import React from "react";
import { IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  IconBloodCount,
  IconBloodSugar,
  IconHormones,
  IconInflammation,
  IconIronStudies,
  IconKidney,
  IconLipid,
  IconLiver,
  IconOthers,
  IconThyroid,
  IconUrine,
  IconVitamin,
} from "@/components/icons";
import {
  ConstellationGroup,
  LabParameterGroupId,
} from "@/types/lab-insights";

import styles from "./MLabGroupTile.module.css";

export type LabGroupStatusKind = "flagged" | "allGood" | "noResults";

// Custom 20×20 lab-group icons built from Figma 01_Protons (file
// JbI5xLbqpe8LIg4pXlY5Vl). Each icon is filled (not stroked) and picks up
// its tint from the `color` prop via the `fill` attribute set on
// `IconBase`. Pet groups reuse the closest human-anatomy icon.
export const ICON_BY_GROUP: Record<
  LabParameterGroupId,
  React.ComponentType<{
    size?: number | string;
    color?: string;
  }>
> = {
  [LabParameterGroupId.CBC]: IconBloodCount,
  [LabParameterGroupId.LIPID]: IconLipid,
  [LabParameterGroupId.SUGAR]: IconBloodSugar,
  [LabParameterGroupId.THYROID]: IconThyroid,
  [LabParameterGroupId.LFT]: IconLiver,
  [LabParameterGroupId.KFT]: IconKidney,
  [LabParameterGroupId.VITMIN]: IconVitamin,
  [LabParameterGroupId.IRON]: IconIronStudies,
  [LabParameterGroupId.HORMONES]: IconHormones,
  [LabParameterGroupId.INFLAMM]: IconInflammation,
  [LabParameterGroupId.OTHERS]: IconOthers,
  // Pet groups — share visual cues with the human equivalents but use the
  // _PET keys so the snapshot renders the right title/state for pets.
  [LabParameterGroupId.CBC_PET]: IconBloodCount,
  [LabParameterGroupId.KFT_PET]: IconKidney,
  [LabParameterGroupId.URINE_PET]: IconUrine,
  [LabParameterGroupId.LFT_PET]: IconLiver,
  [LabParameterGroupId.SUGAR_PET]: IconBloodSugar,
  [LabParameterGroupId.THYROID_PET]: IconThyroid,
  [LabParameterGroupId.OTHERS_PET]: IconOthers,
};

export const ICON_COLOR_BY_STATUS: Record<LabGroupStatusKind, string> = {
  flagged: "#D64561",
  allGood: "#36116D",
  noResults: "#AEAEAE",
};

export const statusKindFor = (
  group: Pick<ConstellationGroup, "active" | "flaggedCount">
): LabGroupStatusKind => {
  if (!group.active) return "noResults";
  if (group.flaggedCount > 0) return "flagged";
  return "allGood";
};

/**
 * Pure pill renderer (title + optional chevron + status row). Shared between
 * the standalone tile and the snapshot constellation — the snapshot uses it
 * with independent absolute positioning (not stacked next to an avatar in a
 * flex column).
 */
export const LabGroupPill: React.FC<{
  group: ConstellationGroup;
  /** Show the chevron. The pill is presentational; clicks are handled by the parent. */
  showChevron?: boolean;
  className?: string;
}> = ({ group, showChevron = false, className }) => {
  const { t } = useTranslation();
  const status = statusKindFor(group);
  const statusLabel =
    status === "flagged"
      ? t("labInsights.groupFlagged", { count: group.flaggedCount })
      : status === "allGood"
      ? t("labInsights.groupAllGood")
      : t("labInsights.groupNoResults");

  return (
    <div className={[styles.pill, className ?? ""].filter(Boolean).join(" ")}>
      <div className={styles.pillTopRow}>
        <span
          className={[
            styles.pillTitle,
            styles[`pillTitle--${status}`],
          ]
            .filter(Boolean)
            .join(" ")}
          title={group.groupName}
        >
          {group.groupName}
        </span>
        {showChevron && (
          <span className={styles.pillChevron} aria-hidden>
            <IconChevronRight size={16} stroke={2} />
          </span>
        )}
      </div>
      <div className={styles.statusRow}>
        <span
          className={[styles.statusDot, styles[`statusDot--${status}`]]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        />
        <span
          className={[styles.statusText, styles[`statusText--${status}`]]
            .filter(Boolean)
            .join(" ")}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
};

/**
 * Pure avatar (icon ring) renderer — the 50×50 colored ring around the
 * group's category icon. Shared with the snapshot for independent placement.
 */
export const LabGroupAvatar: React.FC<{
  group: ConstellationGroup;
  className?: string;
}> = ({ group, className }) => {
  const status = statusKindFor(group);
  const Icon = ICON_BY_GROUP[group.groupId] ?? IconOthers;
  return (
    <div
      className={[
        styles.avatarOuter,
        styles[`avatarOuter--${status}`],
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden
    >
      <div
        className={[
          styles.avatarInner,
          styles[`avatarInner--${status}`],
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <Icon size={22} color={ICON_COLOR_BY_STATUS[status]} />
      </div>
    </div>
  );
};

export interface MLabGroupTileProps {
  group: ConstellationGroup;
  iconPosition?: "top" | "bottom";
  /** Compact variant — used inside the snapshot constellation where space is tight. */
  compact?: boolean;
  onClick?: (groupId: LabParameterGroupId) => void;
  className?: string;
}

export const MLabGroupTile: React.FC<MLabGroupTileProps> = ({
  group,
  iconPosition = "top",
  compact = false,
  onClick,
  className,
}) => {
  const status = statusKindFor(group);

  // "No results" groups are intentionally not clickable — there's nothing to
  // drill into. The tile still renders for visibility but the role/handlers
  // are dropped so it doesn't show the focus halo or fire navigation.
  const interactive = status !== "noResults" && Boolean(onClick);
  const handleClick = () => {
    if (!interactive) return;
    onClick?.(group.groupId);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(group.groupId);
    }
  };

  return (
    <div
      className={[
        styles.tile,
        iconPosition === "top"
          ? styles["tile--iconAbove"]
          : styles["tile--iconBelow"],
        compact ? styles["tile--compact"] : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-disabled={!interactive}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      style={interactive ? undefined : { cursor: "default" }}
    >
      <LabGroupAvatar group={group} />
      <LabGroupPill group={group} showChevron={interactive} />
    </div>
  );
};

export default MLabGroupTile;
